import {FC, MouseEvent, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import block from 'bem-cn-lite';
import {Button, Checkbox, Icon, Link, Modal, Text} from '@gravity-ui/uikit';
import {Check} from '@gravity-ui/icons';
import {AppContext} from '@app/context';
import {ClientDetails} from '@app/interfaces/ClientDetails';
import {CApi as userGwApi} from '@services/api';
import {openActualOffer} from '@services/openActualOffer';
import {mergeAcceptedConsent, OFFER_REVISION} from '@services/policyConsent';
import OrdersOnlyArchiveSvg from '@assets/orders-only-archive.svg';

import './ModalBannerPolicy.scss';

const b = block('modal-banner-policy');

const POLICY_URL = 'https://disk.yandex.ru/i/GwmTXHnpkl9zxA';
const ACCEPT_CLOSE_DELAY = 1500;
const SUBMIT_ERROR = 'Не удалось сохранить согласие. Попробуйте ещё раз';

interface ModalBannerPolicyProps {
    visibility: boolean;
    stateModal: (value: boolean) => void;
    onSettled?: (accepted: boolean) => void;
}

export const ModalBannerPolicy: FC<ModalBannerPolicyProps> = ({
    visibility,
    stateModal,
    onSettled
}) => {
    const {
        state: {clientDetails, user},
        emit
    } = useContext(AppContext);
    const isOfferRequired = clientDetails?.isOfferRequired === true;
    const showOfferConsent = isOfferRequired && clientDetails?.acceptedOffer == null;
    const [offerAgreed, setOfferAgreed] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const submittingRef = useRef(false);
    const onSettledRef = useRef(onSettled);
    const stateModalRef = useRef(stateModal);

    onSettledRef.current = onSettled;
    stateModalRef.current = stateModal;

    const isConsentChecked = agreed && (!showOfferConsent || offerAgreed);

    useLayoutEffect(() => {
        if (!visibility) {
            return;
        }

        submittingRef.current = false;
        setOfferAgreed(false);
        setAgreed(false);
        setAccepted(false);
        setIsSubmitting(false);
        setSubmitError('');
    }, [visibility]);

    useEffect(() => {
        if (!accepted) {
            return;
        }

        const timerId = window.setTimeout(() => {
            onSettledRef.current?.(true);
            stateModalRef.current(false);
        }, ACCEPT_CLOSE_DELAY);

        return () => window.clearTimeout(timerId);
    }, [accepted]);

    const onCancel = () => {
        if (accepted || isSubmitting) {
            return;
        }

        onSettledRef.current?.(false);
        stateModal(false);
    };

    const onOfferLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        event.stopPropagation();
        void openActualOffer();
    };

    const publishClientDetails = async (offerAccepted: boolean, personalAccepted: boolean) => {
        const login = user?.client || clientDetails?.login;
        let nextDetails = clientDetails;

        if (login) {
            try {
                const response = await userGwApi().client.clientDetail(login);
                nextDetails = response.data as unknown as ClientDetails;
            } catch (error) {
                console.error('Ошибка при получении данных клиента:', error);
            }
        }

        if (!nextDetails) {
            return;
        }

        emit('updateClientDetails', {
            clientDetails: mergeAcceptedConsent(nextDetails, {
                offer: offerAccepted,
                personal: personalAccepted
            })
        });
    };

    const onAccept = async () => {
        if (!isConsentChecked || accepted || submittingRef.current) {
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        setSubmitError('');

        const hadOffer = clientDetails?.acceptedOffer != null;
        const hadPersonal = clientDetails?.personalDataAgreement != null;
        let offerAccepted = hadOffer;
        let personalAccepted = hadPersonal;

        try {
            const api = userGwApi();

            if (isOfferRequired && !offerAccepted) {
                await api.clientOffer.clientOfferUpdate(OFFER_REVISION, {});
                offerAccepted = true;
            }

            if (!personalAccepted) {
                await api.clientAcceptPersonalDataAgreement.clientAcceptPersonalDataAgreementUpdate(
                    {}
                );
                personalAccepted = true;
            }

            await publishClientDetails(offerAccepted, personalAccepted);
            setAccepted(true);
        } catch (error) {
            console.error('Ошибка при сохранении согласия:', error);

            if (offerAccepted !== hadOffer || personalAccepted !== hadPersonal) {
                await publishClientDetails(offerAccepted, personalAccepted);
            }

            setSubmitError(SUBMIT_ERROR);
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={visibility} onClose={onCancel} className={b()}>
            <div className={b('image')}>
                <img className={b('picture')} src={OrdersOnlyArchiveSvg} alt="" />
            </div>

            <div className={b('body')}>
                <Text variant="subheader-3">Согласие с условиями оформления заказов</Text>

                <div className={b('consents')}>
                    {showOfferConsent && (
                        <Checkbox
                            className={b('consent')}
                            size="l"
                            checked={offerAgreed}
                            disabled={isSubmitting || accepted}
                            onUpdate={setOfferAgreed}
                        >
                            Согласен с условиями{' '}
                            <Link
                                className={b('link')}
                                href="#"
                                underline
                                onClick={onOfferLinkClick}
                            >
                                Оферты
                            </Link>
                        </Checkbox>
                    )}

                    <Checkbox
                        className={b('consent')}
                        size="l"
                        checked={agreed}
                        disabled={isSubmitting || accepted}
                        onUpdate={setAgreed}
                    >
                        Согласен на обработку моих персональных данных в соответствии с{' '}
                        <Link
                            className={b('link')}
                            href={POLICY_URL}
                            target="_blank"
                            underline
                            onClick={(event) => event.stopPropagation()}
                        >
                            Политикой обработки персональных данных
                        </Link>
                    </Checkbox>
                </div>

                {submitError && (
                    <Text variant="body-2" color="danger">
                        {submitError}
                    </Text>
                )}
            </div>

            <div className={b('footer', {accepted})}>
                {!accepted && (
                    <Button
                        size="l"
                        view="flat-secondary"
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Отмена
                    </Button>
                )}
                <Button
                    size="l"
                    view={accepted ? 'normal' : 'action'}
                    className={b('accept', {accepted})}
                    disabled={isSubmitting || (!accepted && !isConsentChecked)}
                    loading={isSubmitting}
                    onClick={onAccept}
                >
                    {accepted && <Icon data={Check} size={16} />}
                    {accepted ? 'Условия приняты' : 'Согласен'}
                </Button>
            </div>
        </Modal>
    );
};
